import { useEffect, useMemo, useState, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import { Grid, Typography } from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import {
  Color,
  RadioButtonGroupController,
  SingleSelect,
  TextField,
  TextFieldController
} from "@bonliva-crm/ui";
import {
  CustomerType,
  useGetConsultantAreaOfExpertisesLazyQuery
} from "@bonliva-crm/graphql";
import { DatePickerController } from "@bonliva-crm/ui/form-controller";
import { getOptions } from "@common/CostCenterSelect";
import { CustomerFullSearchModal } from "@customers-new/customer-search/full/customer-full-search-modal";
import { AvailabilityCalendar } from "@consultants/consultant-profile-new/availability-calendar/availability-calendar";
import { useLocale } from "../../../hooks/useLocale";
import { useCostCenters, useUserProfile } from "../../../hooks/useGlobalStore";
import { UserSelect } from "@containers/user-select/user-select";

type Props = {
  consultantId: string;
  availablityCalendarDisabled?: boolean;
  withUsersSelector: boolean;
};

export function ConsultantOpportunityForm(props: Props) {
  const user = useUserProfile();
  const { locale } = useLocale();

  const projectSelectRef = useRef<any>(null);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);

  const formCtx = useFormContext();

  const [getAoeQuery, aoeQuery] = useGetConsultantAreaOfExpertisesLazyQuery({
    variables: { id: props.consultantId },
    fetchPolicy: "cache-and-network"
  });

  const { costCenters } = useCostCenters();
  const costCenterOptions = useMemo(() => getOptions(costCenters, false), [costCenters]);

  const type = useWatch({ name: "type" });

  useEffect(() => {
    formCtx.setValue("user", null);

    if (type) {
      setTimeout(() => {
        projectSelectRef.current?.focus();
      }, 100);
    }
  }, [type]);

  return (
    <>
      <Grid container spacing={1}>
        <DevTool control={formCtx.control} />

        <Grid
          item
          xs={6}
          style={{ display: "flex", gap: "10px", flexDirection: "column" }}
        >
          <Controller
            name="customer"
            render={({ field }) => (
              <TextField
                required
                label={locale.Organisation}
                onClick={() => setCustomerSearchOpen(true)}
                fullWidth={true}
                InputLabelProps={{
                  shrink: true
                }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <ArrowForward
                      htmlColor={Color.Body}
                      style={{ width: "1rem", height: "1rem" }}
                    />
                  )
                }}
                value={field.value?.Name ?? ""}
              />
            )}
          />

          <Controller
            name="costCenter"
            render={({ field }) => (
              <SingleSelect<LabelValue>
                {...field}
                required
                inputLabel={locale.CostCenter}
                labelShrink={true}
                placeholder={`${locale.Select} ${locale.CostCenter.toLowerCase()}`}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                menuPlacement="auto"
                options={costCenterOptions}
                isClearable={true}
                hideSelectedOptions={false}
                styleProps={{
                  control: { showBorder: true },
                  menu: { showBorder: true }
                }}
              />
            )}
          />

          <Controller
            name="areaOfExpertise"
            render={({ field }) => (
              <SingleSelect<LabelValue>
                {...field}
                inputLabel={locale.Expertise}
                labelShrink={true}
                isLoading={aoeQuery.loading}
                placeholder={`${locale.Select} ${locale.Expertise.toLowerCase()}`}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                onMenuOpen={() => getAoeQuery()}
                menuPlacement="auto"
                options={
                  aoeQuery.data?.consultantById?.AreasOfExpertise.map((x) => ({
                    label: x,
                    value: x
                  })) || []
                }
                isClearable={true}
                hideSelectedOptions={false}
                styleProps={{
                  control: { showBorder: true },
                  menu: { showBorder: true }
                }}
              />
            )}
          />

          <DatePickerController
            name="estimatedResponseDate"
            DatePickerProps={{
              required: true,
              label: locale.estimatedResponseDate,
              fullWidth: true,
              DatePickerProps: {
                required: true
              }
            }}
          />

          <TextFieldController
            name="note"
            label={locale.Notes}
            textFieldProps={{
              fullWidth: true,
              InputLabelProps: {
                shrink: true
              }
            }}
          />

          {props.withUsersSelector && (
            <>
              <RadioButtonGroupController
                label={locale.Project}
                name="type"
                options={[
                  { label: "Eget", value: "own" },
                  { label: "Delat", value: "shared" }
                ]}
                RadioButtonGroupProps={{
                  required: true,
                  RadioGroupProps: {
                    row: true
                  }
                }}
              />

              {formCtx.getValues("type") === "shared" && (
                <div>
                  <Controller
                    name="user"
                    render={({ field }) => (
                      <UserSelect
                        excludeUserIds={[user.id]}
                        value={field.value}
                        onChange={(v) => field.onChange(v)}
                      />
                    )}
                  />
                </div>
              )}
            </>
          )}
        </Grid>

        <Grid item xs={6}>
          <Typography
            variant="body1"
            color="textPrimary"
            style={{ marginLeft: 18, marginBottom: 10 }}
          >
            Välj vilka veckor du kommer presentera:
          </Typography>

          <Controller
            name="weeks"
            render={({ field }) => (
              <AvailabilityCalendar
                hideExpandedView
                consultantId={props.consultantId}
                onSelectWeeks={(value) => field.onChange(value)}
                disabled={props.availablityCalendarDisabled}
              />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name="customer"
        render={({ field }) => (
          <CustomerFullSearchModal
            value={field.value}
            onChange={(value) => field.onChange(value)}
            open={customerSearchOpen}
            setOpen={setCustomerSearchOpen}
            isMulti={false}
            customerTypes={[CustomerType.Region]}
            instantSelection={true}
          />
        )}
      />
    </>
  );
}
